import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  effect,
  inject,
  untracked,
} from '@angular/core';

import {
  TellentAssistantConfigService,
  TellentAssistantControlService,
  TellentAssistantService,
  TellentAssistantThreadsService,
} from '@core/tellent-assistant-logic';
import { injectTellentAssistantMobileKeyboardHeight } from '@core/tellent-assistant-mobile-contracts';
import { TellentAssistantChatPanelComponent } from '@core/tellent-assistant-smart-ui';

import { TellentAssistantMobileExpandedStateService } from './tellent-assistant-mobile-expanded-state.service';

// Toggled on the host ion-modal to grow it (see ModalService.createAssistantSheet
// and dialogs.less). On for every open but a trigger open on an empty thread.
const EXPANDED_CLASS = 'assistant-sheet-modal--expanded';
// Grow this long after focus if no software keyboard has appeared (e.g. a
// hardware keyboard). Longer than a normal keyboard open, so on a touch keyboard
// the keyboard-driven grow wins first.
const GROW_FALLBACK_MS = 500;

// `Element.closest()` doesn't cross shadow boundaries — when this component is hosted inside a
// ShadowDom-encapsulated custom element (e.g. wrapped for a non-Angular host app), the ion-modal
// living in the light DOM above the shadow host is otherwise unreachable. No-op (identical to
// plain closest()) when there's no shadow root in the way, so it's safe for the un-wrapped case too.
function closestAcrossShadowRoots(element: Element, selector: string): Element | null {
  let current: Element | null = element;
  while (current) {
    const match = current.closest(selector);
    if (match) return match;
    const root = current.getRootNode();
    current = root instanceof ShadowRoot ? root.host : null;
  }
  return null;
}

@Component({
  selector: 'tellent-assistant-mobile-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TellentAssistantChatPanelComponent],
  host: {
    '(focusin)': 'onFocusIn($event)',
    '(click)': 'onHostClick($event)',
  },
  template: `<div class="viewport" [style.paddingBottom]="paddingBottom">
    <tellent-assistant-chat-panel mode="compact" [embedded]="true" (close)="control.close()" />
  </div>`,
  styleUrl: './tellent-assistant-mobile-modal.component.less',
})
export class TellentAssistantMobileModalComponent {
  private readonly config = inject(TellentAssistantConfigService);
  private readonly threadsService = inject(TellentAssistantThreadsService);
  private readonly assistant = inject(TellentAssistantService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly keyboardHeight = injectTellentAssistantMobileKeyboardHeight();
  private readonly expandedState = inject(TellentAssistantMobileExpandedStateService);
  private readonly loadStartersOnContextChange = effect(() => {
    const ctx = this.config.currentContext();
    untracked(() => this.config.loadStartersFor(ctx));
  });
  // Once the input has been focused, grow as soon as the keyboard starts to
  // appear (its height flips on keyboardWillShow). Riding the keyboard's own
  // motion keeps the grow and the keyboard-driven resize together, rather than
  // the grow firing ~300ms earlier on focus and reading as a separate jump.
  private readonly growWhenKeyboardOpens = effect(() => {
    if (this.growArmed && this.keyboardHeight() > 0) {
      this.grow();
    }
  });
  // Small is for the empty state alone: a thread picked from the history grows it.
  private readonly growWhenConversationStarts = effect(() => {
    if (this.assistant.messages().length > 0) {
      this.grow();
    }
  });
  // Toggled, not added: manage's React sheet reuses one ion-modal across opens.
  private readonly applyExpandedClass = effect(() => {
    const expanded = this.expandedState.expanded();
    closestAcrossShadowRoots(this.host.nativeElement, 'ion-modal')?.classList.toggle(
      EXPANDED_CLASS,
      expanded,
    );
  });

  protected readonly control = inject(TellentAssistantControlService);
  // KeyboardResize.Ionic already shrinks the modal to sit above the keyboard, so
  // padding it by the keyboard height would double-count and float the input up.
  // Only the closed-keyboard case needs to clear the home indicator / Android
  // navbar; env() is the fallback when the vars are unset.
  protected readonly paddingBottom =
    'var(--rtm-navbar-height, var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))';

  private growArmed = false;
  private growFallbackId?: number;

  constructor() {
    this.threadsService.loadThreads();
    this.destroyRef.onDestroy(() => window.clearTimeout(this.growFallbackId));
  }

  protected onFocusIn(event: FocusEvent): void {
    if (this.expandedState.expanded() || this.growArmed) {
      return;
    }
    const target = <HTMLElement | null>event.target;
    if (!target?.matches('input, textarea')) {
      return;
    }
    // Arm the keyboard-driven grow and start the no-keyboard fallback; whichever
    // fires first grows the sheet once (it never shrinks back).
    this.growArmed = true;
    this.growFallbackId = window.setTimeout(() => this.grow(), GROW_FALLBACK_MS);
  }

  protected onHostClick(event: MouseEvent): void {
    if (this.expandedState.expanded()) {
      return;
    }
    // A starter click sends its canned message straight away without ever
    // focusing the input (see TellentAssistantChatPanelComponent.sendStarter), so
    // there is no keyboard to sync with here — grow immediately.
    const target = <HTMLElement | null>event.target;
    if (!target?.closest('tellent-assistant-empty-state .suggestion')) {
      return;
    }
    this.grow();
  }

  private grow(): void {
    window.clearTimeout(this.growFallbackId);
    this.expandedState.markExpanded();
  }
}
